'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface ServiceSheetModalProps {
  serviceName: string;
  onClose: () => void;
}

const ServiceSheetModal = ({ serviceName, onClose }: ServiceSheetModalProps) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('E-mailadres is verplicht');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Ongeldig e-mailadres');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const supabase = createClient();
      
      // Track download in database
      const { error: dbError } = await supabase
        .from('service_sheet_downloads')
        .insert({
          email: email,
          service_name: serviceName,
        });

      if (dbError) throw dbError;

      // Trigger PDF download
      const pdfFileName = serviceName.toLowerCase().replace(/\s+/g, '-');
      const link = document.createElement('a');
      link.href = `/service-sheets/${pdfFileName}.pdf`;
      link.download = `${pdfFileName}-service-sheet.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(true);
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Error tracking download:', err);
      setError('Er is iets misgegaan. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="glassmorphic rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircleIcon" size={32} className="text-success" />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-2">Download gestart!</h3>
          <p className="text-muted-foreground">
            Het service sheet wordt gedownload. Controleer uw downloads map.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glassmorphic rounded-2xl p-8 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-foreground">Download Service Sheet</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors duration-300"
          >
            <Icon name="XMarkIcon" size={20} className="text-foreground" />
          </button>
        </div>

        <p className="text-muted-foreground mb-6">
          Voer uw e-mailadres in om het <span className="text-primary font-semibold">{serviceName}</span> service sheet te downloaden.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              E-mailadres *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              className="w-full px-4 py-3 rounded-lg bg-muted border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300"
              placeholder="uw.email@bedrijf.nl"
              disabled={isSubmitting}
            />
            {error && (
              <p className="text-destructive text-sm mt-2 flex items-center space-x-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                <span>{error}</span>
              </p>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-lg font-semibold border border-border text-foreground hover:bg-muted transition-all duration-300"
              disabled={isSubmitting}
            >
              Annuleren
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 rounded-lg font-semibold bg-cta text-cta-foreground hover:bg-cta/90 transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Bezig...</span>
                </>
              ) : (
                <>
                  <Icon name="ArrowDownTrayIcon" size={20} />
                  <span>Download</span>
                </>
              )}
            </button>
          </div>
        </form>

        <p className="text-xs text-muted-foreground mt-4 text-center">
          Uw e-mailadres wordt alleen gebruikt voor het verstrekken van het service sheet en wordt niet gedeeld met derden.
        </p>
      </div>
    </div>
  );
};

export default ServiceSheetModal;